import { Checkbox, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { describeSchedule, type Schedule, ScheduleField } from './ScheduleField';

/**
 * The conditions on a discount: when it can be used, by whom, and how often.
 *
 * The stored shape is a list of typed rules, and it used to be edited as raw JSON — which meant
 * knowing that `notCombinableWithTourOperator` is spelt exactly that way and that `maxPerCheck`
 * carries a `max`. The set of rules is CLOSED and small, so this asks the four questions directly
 * instead of offering a generic rule builder: the manager sees every condition that exists, ticks
 * the ones that apply, and cannot invent a rule the register would ignore.
 */

export type EligibilityRule =
  | { type: 'notCombinableWithTourOperator' }
  | { type: 'requiresRole'; detail?: string }
  | { type: 'schedule'; schedule: Schedule }
  | { type: 'maxPerCheck'; max: number };

export function toRules(value: unknown): EligibilityRule[] {
  return Array.isArray(value) ? (value as EligibilityRule[]) : [];
}

/** One line for the table: "Not with tour operators · Staff only · Twice a check". */
export function describeEligibility(value: unknown): string {
  const rules = toRules(value);
  if (rules.length === 0) return 'Anyone';
  return rules
    .map((r) => {
      switch (r.type) {
        case 'notCombinableWithTourOperator':
          return 'Not with tour operators';
        case 'requiresRole':
          return r.detail ? `Staff only (${r.detail})` : 'Staff only';
        case 'schedule':
          return describeSchedule(r.schedule);
        case 'maxPerCheck':
          return r.max === 1 ? 'Once a check' : `${r.max}× a check`;
        default:
          return 'A condition applies';
      }
    })
    .join(' · ');
}

export function EligibilityField({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: unknown;
  onChange: (value: EligibilityRule[]) => void;
}) {
  const rules = toRules(value);
  const find = <T extends EligibilityRule['type']>(type: T) =>
    rules.find((r) => r.type === type) as Extract<EligibilityRule, { type: T }> | undefined;

  /** Rules are kept in a stable order so an edit never reshuffles what is already stored. */
  const replace = (type: EligibilityRule['type'], rule: EligibilityRule | null) => {
    const without = rules.filter((r) => r.type !== type);
    onChange(rule ? [...without, rule] : without);
  };

  const noTourOperator = Boolean(find('notCombinableWithTourOperator'));
  const staffOnly = find('requiresRole');
  const window = find('schedule');
  const perCheck = find('maxPerCheck');

  return (
    <div>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      {description && (
        <Text size="xs" c="dimmed" mb={6}>
          {description}
        </Text>
      )}
      <Stack gap="sm">
        <Checkbox
          label="Not on a tour operator's check"
          description="Stops a group already on an agency rate taking this on top"
          checked={noTourOperator}
          onChange={(e) =>
            replace(
              'notCombinableWithTourOperator',
              e.currentTarget.checked ? { type: 'notCombinableWithTourOperator' } : null,
            )
          }
        />

        <div>
          <Checkbox
            label="A member of staff has to be named"
            description="For a staff discount — someone has to be picked and checked at the register"
            checked={Boolean(staffOnly)}
            onChange={(e) =>
              replace('requiresRole', e.currentTarget.checked ? { type: 'requiresRole' } : null)
            }
          />
          {staffOnly && (
            <TextInput
              size="sm"
              mt="xs"
              ml="xl"
              label="Which staff"
              placeholder="Anyone on the payroll"
              description="Optional — leave empty and any named member of staff qualifies"
              value={staffOnly.detail ?? ''}
              onChange={(e) =>
                replace('requiresRole', {
                  type: 'requiresRole',
                  ...(e.currentTarget.value.trim() ? { detail: e.currentTarget.value } : {}),
                })
              }
            />
          )}
        </div>

        <div>
          <Checkbox
            label="Only at certain times"
            checked={Boolean(window)}
            onChange={(e) =>
              replace(
                'schedule',
                e.currentTarget.checked ? { type: 'schedule', schedule: { rules: [] } } : null,
              )
            }
          />
          {window && (
            <div style={{ marginLeft: 'var(--mantine-spacing-xl)', marginTop: 'var(--mantine-spacing-xs)' }}>
              <ScheduleField
                label=""
                value={window.schedule}
                // An empty schedule here is not "always" — the manager ticked a box asking for a
                // limit, so leaving it blank would silently grant the opposite of what they said.
                emptyMeans="No times set yet, so this condition does nothing until you add one."
                onChange={(schedule) =>
                  replace('schedule', { type: 'schedule', schedule: schedule ?? { rules: [] } })
                }
              />
            </div>
          )}
        </div>

        <div>
          <Checkbox
            label="Limit how many times it can go on one check"
            checked={Boolean(perCheck)}
            onChange={(e) =>
              replace('maxPerCheck', e.currentTarget.checked ? { type: 'maxPerCheck', max: 1 } : null)
            }
          />
          {perCheck && (
            <NumberInput
              size="sm"
              mt="xs"
              ml="xl"
              label="At most"
              min={1}
              allowDecimal={false}
              style={{ width: 160 }}
              value={perCheck.max}
              onChange={(v) => replace('maxPerCheck', { type: 'maxPerCheck', max: Number(v) || 1 })}
            />
          )}
        </div>
      </Stack>
    </div>
  );
}
